import React, { useEffect, useRef, useCallback, useState } from 'react';
import { ScreenCapturePermissionDialog } from './DialogModals';
import { sendProctoringData } from './ExamUtils';
import { uploadScreenshotToS3 } from '../../../utils/s3Upload';
import '../../../styles/screen-sharing-overlays.css';

// Persistent failure tracking using localStorage
const SCREEN_SHARING_STORAGE_KEY = 'screen_sharing_failures';
const MAX_TOTAL_FAILURES = 15; // Maximum failures across all sessions
const MAX_SESSION_FAILURES = 5; // Maximum failures per session
const LOCKOUT_DURATION = 30 * 60 * 1000; // 30 minutes lockout

// Enhanced failure tracking system
const getFailureData = () => {
  try {
    const data = localStorage.getItem(SCREEN_SHARING_STORAGE_KEY);
    return data ? JSON.parse(data) : {
      totalFailures: 0,
      sessionFailures: 0,
      lastFailureTime: null,
      lockoutUntil: null,
      examId: null
    };
  } catch (error) {
    console.error('Error reading failure data:', error);
    return {
      totalFailures: 0,
      sessionFailures: 0,
      lastFailureTime: null,
      lockoutUntil: null,
      examId: null
    };
  }
};

const updateFailureData = (examId) => {
  const data = getFailureData();
  const now = Date.now();
  
  // Reset if different exam
  if (data.examId !== examId) {
    data.examId = examId;
    data.totalFailures = 0;
    data.sessionFailures = 0;
  }
  
  data.totalFailures += 1;
  data.sessionFailures += 1;
  data.lastFailureTime = now;
  
  // Set lockout if too many total failures
  if (data.totalFailures >= MAX_TOTAL_FAILURES) {
    data.lockoutUntil = now + LOCKOUT_DURATION;
  }
  
  try {
    localStorage.setItem(SCREEN_SHARING_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving failure data:', error);
  }
  
  return data;
};

const isLockedOut = () => {
  const data = getFailureData();
  if (!data.lockoutUntil) return false;
  
  const now = Date.now();
  if (now < data.lockoutUntil) {
    return {
      locked: true,
      remainingTime: data.lockoutUntil - now,
      totalFailures: data.totalFailures
    };
  }
  
  // Lockout expired, reset data
  data.lockoutUntil = null;
  data.totalFailures = 0;
  data.sessionFailures = 0;
  try {
    localStorage.setItem(SCREEN_SHARING_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error clearing lockout:', error);
  }
  
  return { locked: false };
};

const clearFailureData = () => {
  try {
    localStorage.removeItem(SCREEN_SHARING_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing failure data:', error);
  }
};

// Enhanced custom overlay functions for better screen sharing guidance
const createCustomOverlay = () => {
  const overlay = document.createElement('div');
  overlay.id = 'screen-sharing-instruction-overlay';
  overlay.className = 'screen-sharing-overlay';
  
  overlay.innerHTML = `
    <div class="screen-sharing-content">
      <div class="screen-sharing-icon">🖥️</div>
      <h2 class="screen-sharing-title">Screen Sharing Required</h2>
      <p class="screen-sharing-description">
        Your browser will now ask for screen sharing permission.<br/>
        <strong style="color: #ffeb3b; font-size: 20px;">You MUST select "Entire screen" option.</strong>
      </p>
      <div class="screen-sharing-warning">
        <p class="screen-sharing-warning-text">
          ⚠️ DO NOT select "Chrome tab" or "Window" - Only "Entire screen" is allowed
        </p>
      </div>
      <p class="screen-sharing-note">
        This ensures exam integrity and security during proctoring.
      </p>
      <div style="margin-top: 20px;">
        <div class="screen-sharing-loading"></div>
        <span style="color: #b0b0b0;">Waiting for your selection...</span>
      </div>
    </div>
  `;
  
  return overlay;
};

const showCustomInstructionOverlay = () => {
  // Remove any existing overlay
  const existing = document.getElementById('screen-sharing-instruction-overlay');
  if (existing) existing.remove();
  
  const overlay = createCustomOverlay();
  document.body.appendChild(overlay);
  
  // Auto-hide after 15 seconds in case browser dialog doesn't appear
  setTimeout(() => {
    hideCustomInstructionOverlay();
  }, 15000);
};

const hideCustomInstructionOverlay = () => {
  const overlay = document.getElementById('screen-sharing-instruction-overlay');
  if (overlay) {
    overlay.style.opacity = '0';
    overlay.style.transition = 'opacity 0.3s ease';
    setTimeout(() => overlay.remove(), 300);
  }
};

const showCustomRetryDialog = (currentAttempt, maxAttempts) => {
  const remaining = maxAttempts - currentAttempt;
  const overlay = document.createElement('div');
  overlay.id = 'screen-sharing-retry-overlay';
  overlay.className = 'screen-sharing-overlay';
  
  overlay.innerHTML = `
    <div class="screen-sharing-content screen-sharing-error">
      <div class="screen-sharing-icon">❌</div>
      <h2 class="screen-sharing-title">Wrong Selection Detected</h2>
      <p class="screen-sharing-description">
        You selected a window or tab instead of the entire screen.<br/>
        <strong>You must select "Entire screen" for exam security.</strong>
      </p>
      <div style="background: rgba(255, 235, 59, 0.2); border: 2px solid #ffeb3b; border-radius: 10px; padding: 15px; margin: 20px 0;">
        <p style="color: #ffeb3b; font-weight: bold; margin: 0;">
          Attempts remaining: ${remaining}
        </p>
      </div>
      <p class="screen-sharing-note">
        <div class="screen-sharing-loading"></div>
        Retrying in 2 seconds...
      </p>
    </div>
  `;
  
  document.body.appendChild(overlay);
  
  setTimeout(() => {
    overlay.remove();
  }, 2000);
};

const showCustomErrorDialog = (message) => {
  const overlay = document.createElement('div');
  overlay.id = 'screen-sharing-error-overlay';
  overlay.className = 'screen-sharing-overlay';
  
  overlay.innerHTML = `
    <div class="screen-sharing-content screen-sharing-error">
      <div class="screen-sharing-icon">🚫</div>
      <h2 class="screen-sharing-title">Screen Sharing Required</h2>
      <p class="screen-sharing-description">
        ${message}
      </p>
      <button onclick="window.location.reload()" class="screen-sharing-button" style="margin-top: 20px;">
        Refresh Page & Try Again
      </button>
    </div>
  `;
  
  document.body.appendChild(overlay);
};

const showPersistentFailureDialog = (failureData) => {
  const overlay = document.createElement('div');
  overlay.id = 'persistent-failure-overlay';
  overlay.className = 'screen-sharing-overlay';
  
  overlay.innerHTML = `
    <div class="screen-sharing-content screen-sharing-error">
      <div class="screen-sharing-icon">⚠️</div>
      <h2 class="screen-sharing-title">Multiple Compliance Failures Detected</h2>
      <p class="screen-sharing-description">
        You have failed to comply with screen sharing requirements <strong>${failureData.totalFailures}</strong> times.<br/>
        <strong style="color: #ffeb3b;">This exam requires ENTIRE SCREEN sharing for security.</strong>
      </p>
      <div style="background: rgba(255, 235, 59, 0.2); border: 2px solid #ffeb3b; border-radius: 10px; padding: 20px; margin: 20px 0;">
        <p style="color: #ffeb3b; font-weight: bold; margin: 0; font-size: 16px;">
          ⚠️ WARNING: Continued non-compliance may result in exam disqualification
        </p>
      </div>
      <p style="font-size: 16px; color: #e0e0e0; margin-bottom: 20px;">
        Please contact your exam administrator if you need technical assistance.
      </p>
      <button onclick="window.location.reload()" class="screen-sharing-button" style="margin-top: 20px;">
        Try Again (Last Warning)
      </button>
    </div>
  `;
  
  document.body.appendChild(overlay);
};

const showLockoutDialog = (lockoutInfo) => {
  const remainingMinutes = Math.ceil(lockoutInfo.remainingTime / (60 * 1000));
  
  const overlay = document.createElement('div');
  overlay.id = 'lockout-overlay';
  overlay.className = 'screen-sharing-overlay';
  
  overlay.innerHTML = `
    <div class="screen-sharing-content screen-sharing-error">
      <div class="screen-sharing-icon">🔒</div>
      <h2 class="screen-sharing-title">Exam Access Temporarily Suspended</h2>
      <p class="screen-sharing-description">
        Due to repeated non-compliance with screen sharing requirements, your access has been temporarily suspended.
      </p>
      <div style="background: rgba(244, 67, 54, 0.2); border: 2px solid #f44336; border-radius: 10px; padding: 20px; margin: 20px 0;">
        <p style="color: #f44336; font-weight: bold; margin: 0; font-size: 18px;">
          🔒 Lockout Time Remaining: ${remainingMinutes} minutes
        </p>
      </div>
      <div style="background: rgba(255, 255, 255, 0.1); border-radius: 10px; padding: 20px; margin: 20px 0;">
        <h3 style="color: #fff; margin-top: 0;">What happened?</h3>
        <ul style="color: #e0e0e0; text-align: left; margin: 0;">
          <li>You failed to select "Entire screen" ${lockoutInfo.totalFailures} times</li>
          <li>This violates exam security requirements</li>
          <li>Temporary suspension prevents further violations</li>
        </ul>
      </div>
      <p style="font-size: 16px; color: #ffeb3b; margin-bottom: 20px;">
        <strong>Contact your exam administrator for assistance or wait for the lockout to expire.</strong>
      </p>
      <button onclick="window.location.href='/'" class="screen-sharing-button" style="margin-top: 20px;">
        Return to Dashboard
      </button>
    </div>
  `;
  
  document.body.appendChild(overlay);
  
  // Update countdown every minute
  const countdownInterval = setInterval(() => {
    const newLockoutInfo = isLockedOut();
    if (!newLockoutInfo.locked) {
      clearInterval(countdownInterval);
      overlay.remove();
      window.location.reload();
    } else {
      const newMinutes = Math.ceil(newLockoutInfo.remainingTime / (60 * 1000));
      const timeElement = overlay.querySelector('p[style*="color: #f44336"]');
      if (timeElement) {
        timeElement.innerHTML = `🔒 Lockout Time Remaining: ${newMinutes} minutes`;
      }
    }
  }, 60000);
};

const showExamDisqualificationDialog = () => {
  const overlay = document.createElement('div');
  overlay.id = 'disqualification-overlay';
  overlay.className = 'screen-sharing-overlay';
  
  overlay.innerHTML = `
    <div class="screen-sharing-content screen-sharing-error">
      <div class="screen-sharing-icon">❌</div>
      <h2 class="screen-sharing-title">Exam Disqualified</h2>
      <p class="screen-sharing-description">
        Your exam attempt has been disqualified due to repeated failure to comply with screen sharing security requirements.
      </p>
      <div style="background: rgba(244, 67, 54, 0.3); border: 2px solid #f44336; border-radius: 10px; padding: 20px; margin: 20px 0;">
        <h3 style="color: #f44336; margin-top: 0;">Reason for Disqualification:</h3>
        <ul style="color: #f44336; text-align: left; margin: 0; font-weight: bold;">
          <li>Persistent refusal to enable entire screen sharing</li>
          <li>Multiple violations of exam security protocols</li>
          <li>Failure to comply after repeated warnings</li>
        </ul>
      </div>
      <p style="font-size: 16px; color: #e0e0e0; margin-bottom: 20px;">
        Please contact your exam administrator to discuss rescheduling options.
      </p>
      <button onclick="window.location.href='/'" class="screen-sharing-button" style="margin-top: 20px;">
        Return to Dashboard
      </button>
    </div>
  `;
  
  document.body.appendChild(overlay);
};

const ScreenCaptureHandler = ({ 
  sessionId, 
  exam, 
  examCandidateAPI, 
  captureEnabled = false 
}) => {
  const captureIntervalRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const [showPermissionDialog, setShowPermissionDialog] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);

  // Check for lockout on component mount
  useEffect(() => {
    const lockoutInfo = isLockedOut();
    if (lockoutInfo.locked) {
      showLockoutDialog(lockoutInfo);
      return;
    }
  }, []);

  const requestScreenCapture = useCallback(async () => {
    // Check lockout status before attempting
    const lockoutInfo = isLockedOut();
    if (lockoutInfo.locked) {
      showLockoutDialog(lockoutInfo);
      return false;
    }

    try {
      
      let streamObtained = false;
      let attempts = 0;
      const maxAttempts = MAX_SESSION_FAILURES;
      
      while (!streamObtained && attempts < maxAttempts) {
        attempts++;
        
        try {
          // Enhanced constraints to strongly prefer entire screen
          const enhancedConstraints = {
            video: {
              mediaSource: 'screen',
              width: { ideal: 1920, max: 3840 }, // Support higher resolutions
              height: { ideal: 1080, max: 2160 },
              displaySurface: 'monitor', // Strongly prefer monitor
              cursor: 'always',
              resizeMode: 'crop-and-scale'
            },
            audio: false,
            preferCurrentTab: false,
            selfBrowserSurface: 'exclude',
            systemAudio: 'exclude',
            surfaceSwitching: 'exclude', // Prevent switching between surfaces
            monitorTypeSurfaces: 'include' // Include all monitor types
          };

          // Show custom instruction overlay before browser dialog
          showCustomInstructionOverlay();

          streamRef.current = await navigator.mediaDevices.getDisplayMedia(enhancedConstraints);
          
        } catch (modernError) {
          
          // Fallback with standard constraints
          const standardConstraints = {
            video: {
              mediaSource: 'screen',
              width: { ideal: 1920, max: 3840 },
              height: { ideal: 1080, max: 2160 },
              displaySurface: 'monitor'
            },
            audio: false
          };

          // Show instruction overlay for fallback too
          showCustomInstructionOverlay();
          
          streamRef.current = await navigator.mediaDevices.getDisplayMedia(standardConstraints);
        }
        
        // Hide instruction overlay once user makes selection
        hideCustomInstructionOverlay();
        
        // Enhanced validation for entire screen selection
        const videoTrack = streamRef.current.getVideoTracks()[0];
        const settings = videoTrack.getSettings();
        
        
        // Multi-layer validation approach
        let isEntireScreen = false;
        let validationMethod = '';
        
        // Method 1: Check displaySurface property (most reliable)
        if (settings.displaySurface) {
          isEntireScreen = settings.displaySurface === 'monitor';
          validationMethod = 'displaySurface';
        } 
        // Method 2: Dimension-based validation for older browsers
        else {
          const screenWidth = window.screen.width * window.devicePixelRatio;
          const screenHeight = window.screen.height * window.devicePixelRatio;
          const captureWidth = settings.width;
          const captureHeight = settings.height;
          
          
          // More sophisticated dimension checking
          const widthRatio = captureWidth / screenWidth;
          const heightRatio = captureHeight / screenHeight;
          
          // Allow for some tolerance due to scaling and browser chrome
          isEntireScreen = (widthRatio >= 0.9 && heightRatio >= 0.9) || 
                          (captureWidth >= screenWidth * 0.9 && captureHeight >= screenHeight * 0.9);
          validationMethod = 'dimensions';
          
        }
        
        if (!isEntireScreen) {
          console.warn(`📸 User selected window/tab instead of entire screen (detected via ${validationMethod})`);
          
          // Stop the current stream
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
          
          // Update failure tracking
          const failureData = updateFailureData(exam?.id || sessionId);
          
          // Send failure event to backend for audit
          if (examCandidateAPI) {
            examCandidateAPI.sendProctoringData(sessionId, {
              type: "screenSharingViolation",
              timestamp: new Date().toISOString(),
              details: {
                attempt: attempts,
                totalFailures: failureData.totalFailures,
                sessionFailures: failureData.sessionFailures,
                validationMethod: validationMethod,
                selectedSurface: settings.displaySurface || 'unknown'
              }
            }).catch(error => console.error('📸 Error logging violation:', error));
          }
          
          if (attempts >= maxAttempts) {
            // Check if this triggers a lockout
            if (failureData.totalFailures >= MAX_TOTAL_FAILURES) {
              console.error('📸 Maximum total failures reached - user locked out');
              showLockoutDialog(isLockedOut());
              throw new Error('User locked out due to persistent non-compliance');
            } else if (failureData.totalFailures >= 10) {
              // Show warning for high failure count
              console.warn('📸 High failure count - showing persistent failure warning');
              showPersistentFailureDialog(failureData);
              throw new Error('Persistent non-compliance detected');
            } else {
              // Standard max attempts message
              showCustomErrorDialog('Maximum attempts reached. Exam requires entire screen sharing for security purposes. Please refresh the page and try again, making sure to select "Entire screen" option.');
              throw new Error('Entire screen sharing required - max attempts reached');
            }
          } else {
            showCustomRetryDialog(attempts, maxAttempts);
            // Wait a moment before retrying
            await new Promise(resolve => setTimeout(resolve, 2000));
            continue; // Try again
          }
        }
        
        streamObtained = true;
        
        
        // Clear failure data on success
        clearFailureData();
      }
      
      setPermissionGranted(true);
      setShowPermissionDialog(false);
      
      // Enhanced stream monitoring
      streamRef.current.getVideoTracks()[0].onended = () => {
        setPermissionGranted(false);
        stopScreenCapture();
        // Reset to show the original permission dialog instead of restart dialog
        setShowPermissionDialog(true);
      };
      
      return true;
    } catch (error) {
      console.error('📸 Error requesting screen capture:', error);
      setPermissionGranted(false);
      hideCustomInstructionOverlay();
      
      // Handle specific error cases
      if (error.name === 'NotAllowedError') {
        // User clicked Cancel - show the permission dialog again instead of error
        setShowPermissionDialog(true);
      } else if (error.name === 'NotFoundError') {
        // User didn't select anything - show permission dialog again

        setShowPermissionDialog(true);
      } else if (error.message.includes('locked out')) {
        // Already handled by showing lockout dialog
        setShowPermissionDialog(false);
        return false;
      } else {
        // Other errors - show permission dialog to try again
        setShowPermissionDialog(true);
      }
      
      return false;
    }
  }, [sessionId, examCandidateAPI, exam]);

  const captureScreen = useCallback(async () => {

    if (!captureEnabled || !exam?.proctoring?.captureImages || !permissionGranted) {
      return;
    }

    if (!streamRef.current) {
      const success = await requestScreenCapture();
      if (!success) {
        return;
      }
    }

    try {
      // Create canvas to capture frame
      if (!canvasRef.current) {
        canvasRef.current = document.createElement('canvas');
      }

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const video = document.createElement('video');

      video.srcObject = streamRef.current;
      
      // Wait for video to load and then capture
      video.onloadedmetadata = async () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        // Draw current frame to canvas
        ctx.drawImage(video, 0, 0);
        
        // Convert to base64
        const imageData = canvas.toDataURL('image/jpeg', 0.8);
        
        try {
          // Get candidate and exam info for S3 path
          const candidate = await examCandidateAPI.getCandidateProfile();
          const assignedById = candidate.data?.assignedBy || candidate.data?.companyId || exam?.companyId || exam?.createdBy;
          
          if (!assignedById) {
            throw new Error('Cannot determine company ID for S3 path');
          }

          // Upload directly to S3
          const s3Url = await uploadScreenshotToS3(
            imageData,
            assignedById.toString(),
            candidate.data._id.toString(),
            exam._id.toString()
          );


          // Send S3 URL to backend for database storage
          await examCandidateAPI.sendProctoringData(sessionId, {
            type: "screenshot",
            timestamp: new Date().toISOString(),
            s3Url: s3Url,
            reason: "Regular proctoring screenshot"
          });


        } catch (uploadError) {
          console.error('📸 Error uploading screenshot to S3:', uploadError);
          
          // Fallback to original method if S3 upload fails
          await examCandidateAPI.sendProctoringData(sessionId, {
            type: "screenshot",
            timestamp: new Date().toISOString(),
            imageData: imageData,
            reason: "Regular proctoring screenshot (fallback)"
          });
        }
      };

      // Start playing the video
      await video.play();

    } catch (error) {
      console.error('📸 Error capturing screen:', error);
      
      // Send error event
      examCandidateAPI.sendProctoringData(sessionId, {
        type: "screenCaptureError",
        timestamp: new Date().toISOString(),
        details: error.message
      });
    }
  }, [sessionId, examCandidateAPI, exam, captureEnabled, permissionGranted, requestScreenCapture]);

  const startScreenCapture = useCallback(() => {
    
    if (!captureEnabled || !exam?.proctoring?.captureImages || !permissionGranted) {
      return;
    }

    const frequency = exam?.proctoring?.captureFrequency || 10000; // Default 10 seconds in milliseconds
    
    
    // Initial capture after a short delay to ensure stream is ready
    setTimeout(() => {
      captureScreen();
    }, 1000);
    
    // Set up interval for regular captures
    captureIntervalRef.current = setInterval(captureScreen, frequency);
    
  }, [captureScreen, exam, captureEnabled, permissionGranted]);

  const stopScreenCapture = useCallback(() => {
    
    if (captureIntervalRef.current) {
      clearInterval(captureIntervalRef.current);
      captureIntervalRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

  }, []);

  // Show permission dialog when capture is enabled
  useEffect(() => {
    
    if (captureEnabled && exam?.proctoring?.captureImages && !permissionGranted) {
      setShowPermissionDialog(true);
    }
  }, [captureEnabled, exam, permissionGranted]);

  // Start capture when permission is granted
  useEffect(() => {
    
    if (permissionGranted && captureEnabled && exam?.proctoring?.captureImages) {
      startScreenCapture();
    }
  }, [permissionGranted, captureEnabled, exam?.proctoring?.captureImages, startScreenCapture]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      stopScreenCapture();
    };
  }, [stopScreenCapture]);

  // Handle visibility change to restart capture if needed
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden, capture one final screenshot
        captureScreen();
      } else {
        // Page is visible again, ensure capture is running
        if (captureEnabled && exam?.proctoring?.captureImages && permissionGranted && !captureIntervalRef.current) {
          startScreenCapture();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [captureScreen, startScreenCapture, captureEnabled, exam, permissionGranted]);

  return (
    <>
      <ScreenCapturePermissionDialog
        open={showPermissionDialog}
        onPermissionGranted={requestScreenCapture}
        onPermissionDenied={() => {
          setShowPermissionDialog(false);
          setPermissionGranted(false);
        }}
        required={exam?.proctoring?.captureImages}
      />
    </>
  );
};

export default ScreenCaptureHandler;